/**
 * Audio analysis (spec section 18 / 20).
 *
 * Runs entirely in the browser - no cloud analysis. Produces a compact
 * per-frame feature table (level / bass / mid / treble / beat) that the
 * renderer samples by timestamp. This is what makes audio-reactive
 * visuals possible *deterministically*, which a live AnalyserNode
 * cannot do because rendering may run at a different speed than
 * playback.
 */

import { SILENT_AUDIO } from '../scenes/procedural/shared.js';

export const FEATURE_BANDS = ['level', 'bass', 'mid', 'treble', 'beat'];

/** One-pole low-pass state machine, used as the building block. */
function makeOnePole(cutoffHz, sampleRate) {
  const dt = 1 / sampleRate;
  const rc = 1 / (2 * Math.PI * cutoffHz);
  const alpha = dt / (rc + dt);
  let state = 0;
  return {
    process(x) {
      state += alpha * (x - state);
      return state;
    },
    get value() {
      return state;
    },
  };
}

function percentile(sorted, p) {
  if (!sorted.length) return 0;
  const index = Math.min(sorted.length - 1, Math.max(0, Math.floor(sorted.length * p)));
  return sorted[index];
}

/**
 * @param {AudioBuffer} audioBuffer
 * @param {{fps?:number, maxSeconds?:number, normalize?:boolean}} options
 *   `normalize: false` returns the raw band RMS values, which is what the
 *   tests compare and what the diagnostics panel shows. The renderer uses
 *   the normalised table so the visuals react the same on quiet and loud
 *   recordings.
 * @returns {{fps:number, frameCount:number, duration:number, level:Float32Array, bass:Float32Array, mid:Float32Array, treble:Float32Array, beat:Float32Array, silent:boolean}}
 */
export function analyzeAudioBuffer(audioBuffer, { fps = 30, maxSeconds = 3600, normalize = true } = {}) {
  const silent = makeSilentAnalysis(fps, 0);
  if (!audioBuffer || !audioBuffer.length) return silent;

  const sampleRate = audioBuffer.sampleRate;
  const totalSamples = Math.min(audioBuffer.length, Math.floor(sampleRate * maxSeconds));
  const duration = totalSamples / sampleRate;
  const frameCount = Math.max(1, Math.ceil(duration * fps));
  const windowSize = Math.max(1, Math.floor(sampleRate / fps));

  const channels = [];
  for (let c = 0; c < audioBuffer.numberOfChannels; c += 1) {
    channels.push(audioBuffer.getChannelData(c));
  }

  const raw = {
    level: new Float32Array(frameCount),
    bass: new Float32Array(frameCount),
    mid: new Float32Array(frameCount),
    treble: new Float32Array(frameCount),
  };

  const bassFilter = makeOnePole(200, sampleRate);
  const midFilter = makeOnePole(2000, sampleRate);

  let cursor = 0;
  for (let frame = 0; frame < frameCount; frame += 1) {
    const end = Math.min(totalSamples, cursor + windowSize);
    let sumSq = 0;
    let bassSq = 0;
    let midSq = 0;
    let trebleSq = 0;
    let count = 0;

    for (let i = cursor; i < end; i += 1) {
      // Mono mix keeps the maths cheap and the result predictable.
      let sample = 0;
      for (let c = 0; c < channels.length; c += 1) sample += channels[c][i];
      sample /= channels.length;

      const low = bassFilter.process(sample);
      const lowMid = midFilter.process(sample);
      const band = lowMid - low;
      const high = sample - lowMid;

      sumSq += sample * sample;
      bassSq += low * low;
      midSq += band * band;
      trebleSq += high * high;
      count += 1;
    }

    const n = Math.max(1, count);
    raw.level[frame] = Math.sqrt(sumSq / n);
    raw.bass[frame] = Math.sqrt(bassSq / n);
    raw.mid[frame] = Math.sqrt(midSq / n);
    raw.treble[frame] = Math.sqrt(trebleSq / n);
    cursor = end;
  }

  const normalise = (arr) => {
    if (!normalize) return arr;
    const sorted = Array.from(arr).sort((a, b) => a - b);
    const reference = Math.max(1e-6, percentile(sorted, 0.95));
    const out = new Float32Array(arr.length);
    for (let i = 0; i < arr.length; i += 1) out[i] = Math.min(1, arr[i] / reference);
    return out;
  };

  const level = normalise(raw.level);
  const bass = normalise(raw.bass);
  const mid = normalise(raw.mid);
  const treble = normalise(raw.treble);

  // Onset detection on the bass band, then smooth so visuals never strobe.
  const beat = new Float32Array(frameCount);
  const windowFrames = Math.max(2, Math.round(fps * 0.25));
  for (let i = 0; i < frameCount; i += 1) {
    const start = Math.max(0, i - windowFrames);
    let sum = 0;
    for (let j = start; j < i; j += 1) sum += bass[j];
    const mean = i > start ? sum / (i - start) : 0;
    beat[i] = Math.max(0, bass[i] - mean);
  }
  const smoothed = new Float32Array(frameCount);
  for (let i = 0; i < frameCount; i += 1) {
    smoothed[i] = i === 0 ? beat[i] : smoothed[i - 1] * 0.72 + beat[i] * 0.28;
  }
  const beatNorm = normalise(smoothed);

  return { fps, frameCount, duration, level, bass, mid, treble, beat: beatNorm, silent: false };
}

export function makeSilentAnalysis(fps = 30, duration = 0) {
  const frameCount = Math.max(1, Math.ceil(duration * fps));
  return {
    fps,
    frameCount,
    duration,
    level: new Float32Array(frameCount),
    bass: new Float32Array(frameCount),
    mid: new Float32Array(frameCount),
    treble: new Float32Array(frameCount),
    beat: new Float32Array(frameCount),
    silent: true,
  };
}

/**
 * Sample the analysis at a timeline position, linearly interpolating
 * between frames so slow pans of a reactive element stay smooth.
 */
export function sampleFeatures(analysis, seconds) {
  if (!analysis || analysis.silent) return { ...SILENT_AUDIO };
  const position = Math.max(0, seconds) * analysis.fps;
  const i0 = Math.min(analysis.frameCount - 1, Math.floor(position));
  const i1 = Math.min(analysis.frameCount - 1, i0 + 1);
  const t = position - i0;
  const lerp = (arr) => arr[i0] + (arr[i1] - arr[i0]) * t;
  return {
    level: lerp(analysis.level),
    bass: lerp(analysis.bass),
    mid: lerp(analysis.mid),
    treble: lerp(analysis.treble),
    beat: lerp(analysis.beat),
  };
}

/** Live AnalyserNode reader, used for the on-screen preview only. */
export function readLiveFeatures(analyser, buffer) {
  if (!analyser || !buffer) return { ...SILENT_AUDIO };
  analyser.getByteFrequencyData(buffer);
  const binCount = buffer.length;
  const bandAt = (fromHz, toHz, sampleRate, fftSize) => {
    const hzPerBin = sampleRate / 2 / binCount;
    const from = Math.max(0, Math.floor(fromHz / hzPerBin));
    const to = Math.min(binCount - 1, Math.ceil(toHz / hzPerBin));
    let sum = 0;
    for (let i = from; i <= to; i += 1) sum += buffer[i];
    return sum / Math.max(1, (to - from + 1) * 255);
  };
  const sampleRate = analyser.context.sampleRate;
  const fftSize = analyser.fftSize;
  return {
    level: bandAt(20, sampleRate / 2 - 100, sampleRate, fftSize),
    bass: bandAt(20, 250, sampleRate, fftSize),
    mid: bandAt(250, 2000, sampleRate, fftSize),
    treble: bandAt(2000, 12000, sampleRate, fftSize),
    beat: 0,
  };
}

export function describeAnalysis(analysis) {
  if (!analysis || analysis.silent) return 'No analysis available.';
  const peak = analysis.level.reduce((m, v) => Math.max(m, v), 0);
  return `${analysis.frameCount} frames at ${analysis.fps} fps - peak level ${(peak * 100).toFixed(0)}%`;
}
